import { useCallback } from 'react';

export const useCalendarDownload = (showNotification) => {
  const extractCalendarFilename = (contentDisposition) => {
    if (!contentDisposition) return null;
    const utfMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      return decodeURIComponent(utfMatch[1].replace(/["']/g, '').trim());
    }
    const match = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    return match?.[1]?.trim() || null;
  };

  const buildCalendarUrlWithToken = useCallback((calendarUrl) => {
    if (!calendarUrl || typeof window === 'undefined') return calendarUrl || null;
    try {
      const token = window.localStorage.getItem('token');
      const url = new URL(calendarUrl, window.location.origin);
      if (token && !url.searchParams.get('token')) {
        url.searchParams.set('token', token);
      }
      return url.toString();
    } catch (error) {
      return calendarUrl;
    }
  }, []);

  const openCalendarUrl = useCallback((calendarUrl) => {
    const url = buildCalendarUrlWithToken(calendarUrl);
    if (!url || typeof window === 'undefined') return false;
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }, [buildCalendarUrlWithToken]);

  const downloadCalendarFile = useCallback(async (calendarUrl, fallbackName = 'cortate-evento.ics') => {
    const url = buildCalendarUrlWithToken(calendarUrl);
    if (!url) {
      if (showNotification) {
        showNotification('⚠️ Aviso', 'No se encontró el enlace del calendario.', 'info');
      }
      return false;
    }
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
    try {
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const filename =
        extractCalendarFilename(response.headers.get('Content-Disposition')) || fallbackName;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return true;
    } catch (error) {
      console.error('Error downloading calendar:', error);
      if (showNotification) {
        showNotification('❌ Error', 'No se pudo descargar el evento del calendario.', 'destructive');
      }
      return false;
    }
  }, [buildCalendarUrlWithToken, showNotification]);

  return {
    downloadCalendarFile,
    openCalendarUrl,
    getCalendarUrlWithToken: buildCalendarUrlWithToken
  };
};
